import type { ApiKeyScope, IssuedApiKey } from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { SecretKey } from "../components/SecretKey.tsx";
import {
  buildIssueRequest,
  canIssue,
  defaultScopes,
  SCOPES,
  toggleScope as toggleScopeSet,
} from "./onboardingState.ts";

const DOCS_URL = "https://docs.ggcoin.ai";

/** Quickstart snippet shown once a key exists — mirrors the docs site. */
const SNIPPET = `import { GGCoinClient } from "@developerz.ai/ggcoin-sdk";

const gg = new GGCoinClient({
  baseUrl: "https://api.ggcoin.ai",
  apiKey: process.env.GGCOIN_KEY,
});

const walletId = "<wallet-id>";
const { balance } = await gg.getBalance(walletId);`;

/**
 * Developer onboarding (#31): the agent-economy front door. Issue a first API
 * key and copy the quickstart in one screen. Dev billing is deferred until the
 * payment gateway lands (#150) — shown as a clear "coming soon" so the funnel
 * is honest.
 */
export function Onboarding() {
  const { client } = useAuth();
  const [label, setLabel] = createSignal("my-first-integration");
  const [scopes, setScopes] = createSignal<Set<ApiKeyScope>>(defaultScopes());
  const [busy, setBusy] = createSignal(false);
  const [issued, setIssued] = createSignal<IssuedApiKey | null>(null);
  const [error, setError] = createSignal<unknown | null>(null);

  function toggleScope(scope: ApiKeyScope) {
    setScopes((prev) => toggleScopeSet(prev, scope));
  }

  async function onIssue(e: Event) {
    e.preventDefault();
    // Issuing a key is non-idempotent (each call mints a real secret), so guard
    // against re-entry while a request is in flight.
    if (busy() || !canIssue(label(), scopes())) return;
    setBusy(true);
    setError(null);
    // Drop any previously shown secret so a failed retry never leaves a stale
    // one-time key visible.
    setIssued(null);
    try {
      setIssued(await client.issueApiKey(buildIssueRequest(label(), scopes())));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h1>Get started</h1>
      <p class="muted">
        Issue a key, then move coin in a few lines. Build on the closed-loop rail with a native
        agent layer.
      </p>
      <ErrorBanner error={error()} />

      <Show when={issued()}>
        {(key) => (
          <div class="callout" role="status">
            <strong>Copy your key now — it won't be shown again.</strong>
            <SecretKey value={key().key} />
            <button type="button" class="ghost" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        )}
      </Show>

      <section class="card">
        <h3>1 · Issue an API key</h3>
        <form class="form" onSubmit={onIssue}>
          <label for="label">Label</label>
          <input
            id="label"
            value={label()}
            maxLength={80}
            onInput={(e) => setLabel(e.currentTarget.value)}
            required
          />
          <span class="muted">Scopes</span>
          <div class="checks">
            <For each={SCOPES}>
              {(scope) => (
                <label class="check">
                  <input
                    type="checkbox"
                    checked={scopes().has(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                  {scope}
                </label>
              )}
            </For>
          </div>
          <div class="row">
            <button type="submit" class="primary" disabled={busy() || !canIssue(label(), scopes())}>
              {busy() ? "Issuing…" : "Issue key"}
            </button>
          </div>
        </form>
      </section>

      <section class="card">
        <h3>2 · Make your first call</h3>
        <pre class="code">{SNIPPET}</pre>
        <p class="muted">
          Full quickstart, the MCP agent guide, and the API reference live in the{" "}
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            developer docs
          </a>
          .
        </p>
      </section>

      <section class="card">
        <h3>3 · Billing</h3>
        <p class="muted">
          Self-serve developer billing is coming soon. Until then, reach out to top up a sandbox
          balance for testing.
        </p>
      </section>
    </section>
  );
}
